import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useAuthStore } from "@/store/authStore";
import { useAppStore } from "@/store/appStore";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

let socket = null;

export function useSocket() {
  const { token, isAuthenticated } = useAuthStore();
  const showReward = useAppStore((s) => s.showReward);
  const queryClient = useQueryClient();
  const initialized = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !token || initialized.current) return;
    initialized.current = true;

    const backendUrl = import.meta.env.VITE_API_URL?.replace("/api", "") || window.location.origin;
    socket = io(backendUrl, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => { /* connected */ });
    socket.on("disconnect", () => { /* disconnected */ });

    socket.on("xp:update", ({ xp, coins, reason }) => {
      // Instantly update TopBar without waiting for API round-trip
      useAuthStore.getState().updateStudentStats({ xp, coins });
      showReward({ xp, coins, reason });
      queryClient.invalidateQueries({ queryKey: ["student", "me"] });
    });

    socket.on("message:new", () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    });

    socket.on("notification:new", ({ title, body, emoji }) => {
      toast(`${emoji} ${title}`, { description: body });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    });

    socket.on("shop:purchased", ({ item }) => {
      // Also refresh store so coin balance updates immediately after purchase
      useAuthStore.getState().refreshMe();
      toast.success(`${item.emoji} ${item.name} in deinem Inventar!`);
      queryClient.invalidateQueries({ queryKey: ["student", "me"] });
    });

    return () => {
      socket?.disconnect();
      socket = null;
      initialized.current = false;
    };
  }, [isAuthenticated, token]);

  return socket;
}

export function getSocket() { return socket; }
